import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { submitContactMessage } from "../lib/contact.functions";

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: "Contact Acadly | Corrections, Feedback and Enquiries" },
      {
        name: "description",
        content:
          "Get in touch with Acadly for corrections, topic requests, feedback or privacy questions. Reach the editor directly by email or through the contact form.",
      },
      { property: "og:title", content: "Contact Acadly" },
      {
        property: "og:description",
        content: "Email and contact form for Acadly readers.",
      },
      { property: "og:url", content: "https://acadly.in/contact" },
    ],
    links: [{ rel: "canonical", href: "https://acadly.in/contact" }],
  }),
  component: ContactPage,
});

function ContactPage() {
  const submit = useServerFn(submitContactMessage);
  const [status, setStatus] = useState<
    { state: "idle" } | { state: "sending" } | { state: "sent" } | { state: "error"; message: string }
  >({ state: "idle" });
  return (
    <div className="mx-auto max-w-3xl px-5 py-16 md:py-20 prose-post">
      <div className="stamp mb-6">Contact</div>
      <h1
        className="text-5xl md:text-6xl leading-[1] tracking-tight font-semibold"
        style={{ fontFamily: "var(--font-display)" }}
      >
        Contact Acadly
      </h1>
      <p className="mono-label !mt-2">Last updated: 25 July 2026</p>

      <p>
        Every message reaches a real person. Whether you have spotted a factual
        error, want to request a topic, would like to share feedback, or have a
        question about privacy or the terms of use, we would like to hear from
        you.
      </p>

      <h2>Email</h2>
      <ul>
        <li>
          General enquiries, corrections and topic requests:{" "}
          <a href="mailto:hello@acadly.in">hello@acadly.in</a>
        </li>
        <li>
          Privacy questions, data access and deletion requests:{" "}
          <a href="mailto:hello@acadly.in">hello@acadly.in</a>
        </li>
      </ul>
      <p>
        Replies usually go out within two to three working days. Response times
        can be longer during examination months.
      </p>

      <h2>What to include</h2>
      <p>
        Messages are easier to answer when they include the URL of the article
        in question, the point that needs correcting or clarifying, and any
        reference that supports the change. For privacy requests, please state
        which right you are exercising so it can be routed correctly.
      </p>

      <h2>Send a message</h2>
      <p>
        The form below sends your message directly to the editor. If you would
        rather use your own email client, write to{" "}
        <a href="mailto:hello@acadly.in">hello@acadly.in</a> instead.
      </p>
      {status.state === "sent" ? (
        <p className="text-grade">
          Thanks for writing in. Your message has been received. A reply
          usually follows within two to three working days.
        </p>
      ) : (
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            const form = e.currentTarget;
            const data = new FormData(form);
            setStatus({ state: "sending" });
            try {
              await submit({
                data: {
                  name: String(data.get("name") ?? ""),
                  email: String(data.get("email") ?? ""),
                  subject: String(data.get("subject") ?? ""),
                  message: String(data.get("message") ?? ""),
                  website: String(data.get("website") ?? ""),
                },
              });
              setStatus({ state: "sent" });
              form.reset();
            } catch (err) {
              const message =
                err instanceof Error
                  ? err.message
                  : "Something went wrong. Please email hello@acadly.in.";
              setStatus({ state: "error", message });
            }
          }}
          className="not-prose grid gap-4 mt-6"
        >
          {/* Honeypot: hidden from humans, catches naive bots. */}
          <label
            aria-hidden="true"
            className="absolute left-[-9999px] w-px h-px overflow-hidden"
            tabIndex={-1}
          >
            <span>Leave this field empty</span>
            <input
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
            />
          </label>
          <label className="grid gap-1">
            <span className="mono-label">Your name</span>
            <input
              name="name"
              required
              maxLength={100}
              className="border border-ink bg-transparent px-3 py-2 focus:border-grade outline-none"
            />
          </label>
          <label className="grid gap-1">
            <span className="mono-label">Email</span>
            <input
              type="email"
              name="email"
              required
              maxLength={255}
              className="border border-ink bg-transparent px-3 py-2 focus:border-grade outline-none"
            />
          </label>
          <label className="grid gap-1">
            <span className="mono-label">Subject</span>
            <input
              name="subject"
              maxLength={200}
              className="border border-ink bg-transparent px-3 py-2 focus:border-grade outline-none"
            />
          </label>
          <label className="grid gap-1">
            <span className="mono-label">Message</span>
            <textarea
              name="message"
              required
              minLength={10}
              maxLength={4000}
              rows={6}
              className="border border-ink bg-transparent px-3 py-2 focus:border-grade outline-none resize-y"
            />
          </label>
          {status.state === "error" && (
            <p className="text-grade text-sm">{status.message}</p>
          )}
          <button
            type="submit"
            disabled={status.state === "sending"}
            className="mono-label border border-ink px-4 py-2 hover:border-grade hover:text-grade transition-colors disabled:opacity-50 justify-self-start"
          >
            {status.state === "sending" ? "Sending…" : "Send message"}
          </button>
        </form>
      )}

      <h2>What we do not do here</h2>
      <p>
        Acadly does not offer paid tuition, one-to-one coaching, admission
        counselling, question-paper leaks, or homework and assignment
        completion services. Requests for any of these will not receive a
        reply. Please do not send login credentials, official identity
        documents, or examination material to any Acadly address.
      </p>
    </div>
  );
}